import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useUIStore } from '@/store/uiStore';
import _, { head, set } from 'lodash';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  Users,
  Calendar,
  Clock,
  TrendingUp,
  Activity,
  UserPlus,
  FileText,
  CheckSquare,
  RefreshCw,
} from 'lucide-react';
import axiosInstance from '../Utils/axiosInstance';
import { useHasPermission } from '../Utils/helpers.js';

import { AppLocalEnum, getText } from '../translate/text';

const AnalyticsPage = () => {
  const { setLoading } = useUIStore();
  const [dateRange, setDateRange] = useState('day'); // 'day', 'week', 'month'
  const [startDate, setStartDate] = useState(
    new Date(new Date().setMonth(new Date().getMonth() - 1))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [selectedProvider, setSelectedProvider] = useState('');
  const [availableProviders, setAvailableProviders] = useState([]);

  // Color mapping for ticket statuses
  const STATUS_COLORS = {
    pending: '#FFA500', // Orange
    completed: '#4CAF50', // Green
    cancelled: '#F44336', // Red
    'in-progress': '#2196F3', // Blue
    onHold: '#9C27B0', // Purple
    unknown: '#757575', // Gray
  };

  // State for storing analytics data
  const [metrics, setMetrics] = useState({
    totalTickets: 0,
    completedTickets: 0,
    averageResponseTime: 0,
    patientSatisfaction: 0,
    malePercentage: 0,
    femalePercentage: 0,
    ageGroups: {},
    totalUsers: 0,
  });

  const [ticketStatusData, setTicketStatusData] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [trends, setTrends] = useState({
    appointmentDistribution: [],
    providerPerformance: [],
  });
  const { hasPermission, loading } = useHasPermission('view_analytics');

  // Helper function to group tickets by time period for timeline chart
  // returns an object with keys as the date and values as an array of tickets
  const groupTicketsByPeriod = (tickets, period) => {
    return _.groupBy(tickets, (ticket) => {
      const date = ticket.data().createdAt.toDate();
      switch (period) {
        case 'day':
          return date.toISOString().split('T')[0];
        case 'week':
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() - date.getDay());
          return weekStart.toISOString().split('T')[0];
        case 'month':
        default:
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }
    });
  };

  // Helper function to calculate patient demographics
  /**
   *  male - female - total count
   *  age groups (1-18, 18-25, 26-35, 36-45, 46-55, 55+)
   *
   * @param {*} patients
   * @returns
   */
  const calculatePatientDemographics = (patients) => {
    const currentDate = new Date();
    const maleCount = patients.filter(
      (patient) => patient.data().gender?.toLowerCase() === 'male'
    ).length;
    const femaleCount = patients.filter(
      (patient) => patient.data().gender?.toLowerCase() === 'female'
    ).length;
    const totalPatients = patients.length;

    // Initialize age groups with zeros
    const defaultAgeGroups = {
      '1-18': 0,
      '18-25': 0,
      '26-35': 0,
      '36-45': 0,
      '46-55': 0,
      '55+': 0,
    };

    const ageGroups = patients.reduce(
      (acc, patient) => {
        const data = patient.data();
        const birthdateStr = data.dateOfBirth || data.birthDate;
        if (!birthdateStr) return acc;

        let birthDateObj;
        if (birthdateStr instanceof Timestamp) {
          birthDateObj = birthdateStr.toDate();
        } else {
          birthDateObj = new Date(birthdateStr);
        }

        if (isNaN(birthDateObj.getTime())) return acc;

        let age = currentDate.getFullYear() - birthDateObj.getFullYear();
        const monthDiff = currentDate.getMonth() - birthDateObj.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < birthDateObj.getDate())) {
          age--;
        }

        if (age >= 0 && age <= 18) acc['1-18']++;
        else if (age <= 25) acc['18-25']++;
        else if (age <= 35) acc['26-35']++;
        else if (age <= 45) acc['36-45']++;
        else if (age <= 55) acc['46-55']++;
        else acc['55+']++;

        return acc;
      },
      { ...defaultAgeGroups }
    );

    return {
      malePercentage: totalPatients > 0 ? (maleCount / totalPatients) * 100 : 0,
      femalePercentage: totalPatients > 0 ? (femaleCount / totalPatients) * 100 : 0,
      ageGroups,
      totalPatients,
    };
  };

  const calculateAverageResponseTime = (tickets) => {
    const responseTimes = tickets
      .map((doc) => {
        const data = doc.data();
        if (data.createdAt && data.firstResponseAt) {
          return data.firstResponseAt.toDate() - data.createdAt.toDate();
        }
        return null;
      })
      .filter((time) => time !== null);

    return responseTimes.length > 0
      ? responseTimes.reduce((acc, time) => acc + time, 0) / responseTimes.length
      : 0;
  };

  // const calculatePatientSatisfaction = async (tickets) => {
  //   const ratings = tickets
  //     .map((doc) => doc.data().satisfactionRating)
  //     .filter((rating) => typeof rating === 'number' && rating >= 1 && rating <= 5);

  //   return ratings.length > 0
  //     ? (ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length) * 20
  //     : 0;
  // };

  // const calculateProviderPerformance = async (startTimestamp, endTimestamp) => {
  //   const providers = await getDocs(
  //     query(
  //       collection(db, 'users'),
  //       where('role', 'in', ['iSj9qf44eLxNo53qKb9G', 'dLd6lkmQ9Bf090hZtu2Usz6XBYN2'])
  //     )
  //   );

  // const providerTicketStatusData = await Promise.all(
  //   providers.docs.map(async (provider) => {
  //     const providerTicketsQuery = query(
  //       collection(db, 'tickets'),
  //       where('providerId', '==', provider.id),
  //       where('createdAt', '>=', startTimestamp),
  //       where('createdAt', '<=', endTimestamp)
  //     );

  //     const ticketsSnapshot = await getDocs(providerTicketsQuery);

  //     // Group tickets by status
  //     const statusCounts = ticketsSnapshot.docs.reduce((acc, doc) => {
  //       const status = doc.data().status || 'Unknown';
  //       acc[status] = (acc[status] || 0) + 1;
  //       return acc;
  //     }, {});

  //     return {
  //       provider: provider.data().name,
  //       ...statusCounts,
  //       total: ticketsSnapshot.docs.length,
  //     };
  //   })
  // );

  // // Fetch available providers
  // const providerNames = providerTicketStatusData
  //   .filter((p) => p.total > 0)
  //   .map((p) => p.provider);
  // setAvailableProviders(providerNames);

  //   return providerTicketStatusData;
  // };

  const GetUsersAnalysis = async () => {
    axiosInstance
      .get(
        '/analysis/users?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}'
      )
      // await axios
      // .get(
      // `http://localhost:8000/analysis/users?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`
      // )
      .then((response) => {
        const user_count = response.data.data.users_count;
        const males_count = response.data.data.males_count;
        const females_count = response.data.data.females_count;
        const age_groups = response.data.data.age_groups;

        const totalPatients = user_count;
        const malePercentage = ((males_count / user_count) * 100).toFixed(2);
        const femalePercentage = ((females_count / user_count) * 100).toFixed(2);
        const ageGroups = age_groups;

        const users_data = {
          malePercentage: malePercentage,
          femalePercentage: femalePercentage,
          ageGroups: ageGroups,
        };
        return users_data;
      })
      .catch((error) => {
        console.error('Error fetching user analysis:', error);
      });
  };

  const groupTicketsByPeriod_new = (tickets, period) => {
    const filtered_tickets = tickets.filter((ticket) => ticket.createdAt);
    return _.groupBy(filtered_tickets, (ticket) => {
      const date = new Date(ticket.createdAt);
      switch (period) {
        case 'day':
          return date.toISOString().split('T')[0];
        case 'week':
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() - date.getDay());
          return weekStart.toISOString().split('T')[0];
        case 'month':
        default:
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }
    });
  };

  const calculateAverageResponseTime_new = (tickets) => {
    const responseTimes = tickets
      .map((doc) => {
        const data = doc;
        if (data.createdAt && data.firstResponseAt) {
          return new Date(data.firstResponseAt) - new Date(data.createdAt);
        }
        return null;
      })
      .filter((time) => time !== null);
    return responseTimes.length > 0
      ? responseTimes.reduce((acc, time) => acc + time, 0) / responseTimes.length
      : 0;
  };

  const SyncData = async () => {
    setLoading(true);
    await GetTicketsAnalysis('true');
    setLoading(false);
  };

  const GetTicketsAnalysis = async (update_cache) => {
    let users_data = {};
    setLoading(true);
    await axiosInstance
      .get(
        `/analysis/users?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}&update=${update_cache}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: localStorage.getItem('token'),
          },
        }
      )
      // await axios
      //   .get(
      //     `http://localhost:8000/analysis/users?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}&update=${update_cache}`,
      //     {
      //       headers: {
      //         'Content-Type': 'application/json',
      //         Authorization: localStorage.getItem('token'),
      //       },
      //     }
      //   )
      .then((response) => {
        const user_count = response.data.data.users_count;
        const males_count = response.data.data.males_count;
        const females_count = response.data.data.females_count;
        const age_groups = response.data.data.age_groups;

        const totalPatients = user_count;
        const malePercentage = ((males_count / user_count) * 100).toFixed(2);
        const femalePercentage = ((females_count / user_count) * 100).toFixed(2);
        const ageGroups = age_groups;

        const data = {
          malePercentage: malePercentage,
          femalePercentage: femalePercentage,
          ageGroups: ageGroups,
          totalPatients: totalPatients,
        };
        users_data = data;
      })
      .catch((error) => {
        console.error('Error fetching user analysis:', error);
      });

    let tickets_data = {};
    await axiosInstance
      .get(
        `/analysis/tickets?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}&provider_id=${selectedProvider}&update=${update_cache}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: localStorage.getItem('token'),
          },
        }
      )
      // await axios
      //   .get(
      //     `http://localhost:8000/analysis/tickets?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}&provider_id=${selectedProvider}&update=${update_cache}`,
      //     {
      //       headers: {
      //         'Content-Type': 'application/json',
      //         Authorization: localStorage.getItem('token'),
      //       },
      //     }
      //   )
      .then((response) => {
        const totalTickets = response.data.data.tickets_count;
        const completedTickets = response.data.data.completed_count;
        const tickets = response.data.data.tickets;
        const avgResponseTime = calculateAverageResponseTime_new(tickets);

        const statusCounts = tickets.reduce((acc, doc) => {
          const status = doc.status || 'unknown';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {});
        const ticketStatusChartData = Object.entries(statusCounts).map(([status, count]) => ({
          status: status.charAt(0).toUpperCase() + status.slice(1),
          count: count,
        }));
        setTicketStatusData(ticketStatusChartData);

        const appointmentTypes = tickets.reduce((acc, doc) => {
          const type = doc.appoint_type;
          if (type) {
            acc[type] = (acc[type] || 0) + 1;
          }
          return acc;
        }, {});

        const appointmentDistribution = Object.entries(appointmentTypes).map(([type, count]) => ({
          type,
          count,
        }));

        appointmentDistribution.forEach((item) => {
          item.type = getText(item.type);
        });

        setTrends({
          appointmentDistribution,
        });

        const groupedTickets = groupTicketsByPeriod_new(tickets, dateRange);
        let timelineChartData = Object.entries(groupedTickets)
          .map(([date, tickets]) => ({
            date,
            count: tickets.length,
            completed: tickets.filter((t) => t.status === 'completed').length,
            pending: tickets.filter((t) => t.status === 'pending').length,
            cancelled: tickets.filter((t) => t.status === 'cancelled').length,
            in_progress: tickets.filter((t) => t.status === 'in-progress').length,
          }))
          .sort((a, b) => a.date.localeCompare(b.date));

        setTimelineData(timelineChartData);

        tickets_data = {
          totalTickets: totalTickets,
          completedTickets: completedTickets,
          averageResponseTime: avgResponseTime,
        };
      })
      .catch((error) => {
        console.error('Error fetching ticket analysis:', error);
      });

    await axiosInstance
      .get('/doctorsappuser?role=doctor', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('token'),
          lang: localStorage.getItem('language') || 'en',
        },
      })
      // await axios
      //   .get(`http://localhost:8000/doctorsappuser?role=doctor`, {
      //     headers: {
      //       'Content-Type': 'application/json',
      //       Authorization: localStorage.getItem('token'),
      //     },
      //   })
      .then((response) => {
        const providers = response.data.data;
        const providerNames = providers.map((provider) => provider.name);
        setAvailableProviders(providers);
      });
    setMetrics({
      totalTickets: tickets_data.totalTickets,
      completedTickets: tickets_data.completedTickets,
      averageResponseTime: tickets_data.averageResponseTime,
      malePercentage: users_data.malePercentage,
      femalePercentage: users_data.femalePercentage,
      ageGroups: users_data.ageGroups,
      totalUsers: users_data.totalPatients,
    });
    setLoading(false);
  };

  const [lang, setLang] = useState(localStorage.getItem('language') || AppLocalEnum.EN);
  useEffect(() => {
    setLang(localStorage.getItem('language') || AppLocalEnum.EN);
  }, [localStorage.getItem('language')]);

  // Fetch analytics data when filters change (after permission check is complete)
  useEffect(() => {
    // Wait for permission check to complete before fetching
    if (loading) return;

    // Only fetch if user has permission
    if (!hasPermission) return;

    GetTicketsAnalysis('false');
  }, [startDate, endDate, dateRange, selectedProvider, hasPermission, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500"></div>
          <p className="mt-4 text-gray-700 dark:text-gray-300 text-lg">{getText('loading')}</p>
        </div>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 text-center">
          <h2 className="text-2xl font-semibold text-red-600 dark:text-red-400">
            {getText('access_denied')}
          </h2>
          <p className="mt-2 text-gray-700 dark:text-gray-300">
            {getText('you_do_not_have_permission_to_access_this_page')}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div
      className="container mx-auto px-4 py-8"
      style={{
        direction: lang == AppLocalEnum.AR ? 'rtl' : 'ltr',
      }}
    >
      {/* Header with Date Filters */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {getText('analytics_dashboard')}
          </h1>
          <div className="flex gap-4 items-center">
            {/* Existing Date Range Inputs */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate.toISOString().split('T')[0]}
                onChange={(e) => setStartDate(new Date(e.target.value))}
                className="rounded border p-2"
              />
              <span>{getText('to')}</span>
              <input
                type="date"
                value={endDate.toISOString().split('T')[0]}
                onChange={(e) => setEndDate(new Date(e.target.value))}
                className="rounded border p-2"
              />
            </div>

            {/* Provider Filter */}
            <select
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              className={`rounded border p-2`}
              style={{
                direction: lang === AppLocalEnum.AR ? 'ltr' : 'ltr',
              }}
            >
              <option value="">{getText('all_providers')}</option>
              {availableProviders.map((provider) => (
                <option key={provider._id} value={provider._id}>
                  {provider.name}
                </option>
              ))}
            </select>

            {/* Date Range Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setDateRange('day')}
                className={`px-3 py-1 rounded ${dateRange === 'day' ? 'bg-primary-500 text-white' : 'bg-gray-200'}`}
              >
                {getText('daily')}
              </button>
              <button
                onClick={() => setDateRange('week')}
                className={`px-3 py-1 rounded ${dateRange === 'week' ? 'bg-primary-500 text-white' : 'bg-gray-200'}`}
              >
                {getText('weekly')}
              </button>
              <button
                onClick={() => setDateRange('month')}
                className={`px-3 py-1 rounded ${dateRange === 'month' ? 'bg-primary-500 text-white' : 'bg-gray-200'}`}
              >
                {getText('monthly')}
              </button>
            </div>
            <button
              onClick={SyncData}
              className={`px-2 py-2 rounded text-white bg-gray-100 cursor-pointer`}
            >
              <RefreshCw size="16" className=" text-black" />
            </button>
          </div>
        </div>
      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
          style={{
            direction: lang == AppLocalEnum.AR ? 'rtl' : 'ltr',
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {getText('total_appointments')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {metrics.totalTickets}
              </p>
            </div>
            <Users className="w-12 h-12 text-primary-500" />
          </div>
          <div className="mt-4">
            <div className="flex items-center">
              <div className="flex-1">
                <div className="h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-primary-500 rounded-full"
                    style={{
                      width: `${metrics.totalTickets > 0 ? (metrics.completedTickets / metrics.totalTickets) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
              <span
                className={`ml-2 text-sm text-gray-600 dark:text-gray-400 ${lang == AppLocalEnum.AR ? 'mx-2' : ''}`}
              >
                {metrics.totalTickets > 0
                  ? Math.round((metrics.completedTickets / metrics.totalTickets) * 100)
                  : 0}
                % {getText('total')}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div
            className="flex items-center justify-between"
            style={{
              direction: lang == AppLocalEnum.AR ? 'rtl' : 'ltr',
            }}
          >
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {getText('completed_appointments')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {metrics.completedTickets}
              </p>
            </div>
            <CheckSquare className="w-12 h-12 text-primary-500" />
          </div>
          <div className="mt-4">
            <div className="flex items-center">
              <div className="flex-1">
                <div className="h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-primary-500 rounded-full"
                    style={{
                      width: `${metrics.totalTickets ? (metrics.completedTickets / metrics.totalTickets) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
              <span
                className={`ml-2 text-sm text-gray-600 dark:text-gray-400 ${lang == AppLocalEnum.AR ? 'mx-2' : ''}`}
              >
                {getText('completion_rate')}
              </span>
            </div>
          </div>
        </div>

        <div
          className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 `}
          style={{
            direction: lang == AppLocalEnum.AR ? 'rtl' : 'ltr',
          }}
        >
          <div
            className={`flex items-center justify-between ${lang === AppLocalEnum.AR ? 'rtl' : 'ltr'}`}
          >
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {getText('avg_response_time')}
              </p>
              <div className={`flex ${lang == AppLocalEnum.AR ? ' items-center' : ''}`}>
                <p className={`text-2xl font-bold text-gray-900 dark:text-white  inline`}>
                  {Math.round(metrics.averageResponseTime / (1000 * 60))}
                </p>
                <p className="inline mx-2">{getText('m')}</p>
              </div>
            </div>
            <Clock className="w-12 h-12 text-primary-500" />
          </div>
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            {getText('average_time_to_first_response')}
          </p>
        </div>

        {false && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Satisfaction Rate
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Math.round(metrics.patientSatisfaction)}%
                </p>
              </div>
              <TrendingUp className="w-12 h-12 text-primary-500" />
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Based on ticket ratings</p>
          </div>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ticket Status Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {getText('tickets_by_status')}
          </h3>
          <div className="h-80" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ticketStatusData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="status" tickFormatter={(status) => getText(status.toLowerCase())} />
                <YAxis />
                <Tooltip
                  labelFormatter={(label) => getText(label.toLowerCase())}
                  formatter={(value) => [value, getText('count')]}
                />
                <Legend formatter={(value) => getText(value)} />
                <Bar dataKey="count" name={getText('count')}>
                  {ticketStatusData.map((entry) => (
                    <Cell
                      key={entry.status}
                      fill={STATUS_COLORS[entry.status?.toLowerCase()] || STATUS_COLORS.Unknown}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Appointment Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {getText('appointment_distribution')}
          </h3>
          <div className="h-80" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={trends.appointmentDistribution}
                  dataKey="count"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {trends.appointmentDistribution.map((entry, index) => {
                    const colors = [
                      '#0088FE',
                      '#00C49F',
                      '#FFBB28',
                      '#FF8042',
                      '#8884D8',
                      '#82CA9D',
                    ];
                    return <Cell key={index} fill={colors[index % colors.length]} />;
                  })}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Timeline Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 col-span-2">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {getText('appointment_timeline')}
          </h3>
          <div className="h-80" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timelineData} className="pb-4">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ angle: -45 }} textAnchor="end" height={70} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#8884d8"
                  name={getText('total_appointments')}
                />
                <Line
                  type="monotone"
                  dataKey="completed"
                  stroke={STATUS_COLORS.Completed}
                  name={getText('completed')}
                />
                <Line
                  type="monotone"
                  dataKey="pending"
                  stroke={STATUS_COLORS.Pending}
                  name={getText('pending')}
                />
                <Line
                  type="monotone"
                  dataKey="cancelled"
                  stroke={STATUS_COLORS.Cancelled}
                  name={getText('cancelled')}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Provider Performance */}
        {false && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Provider Performance
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trends.providerPerformance} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis dataKey="provider" type="category" />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="completionRate"
                    fill={STATUS_COLORS.Completed}
                    name="Completion Rate (%)"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        {/* Patient Demographics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {getText('patient_demographics')}
          </h3>
          <div className="space-y-4">
            <div className="flex flex-col space-y-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600 dark:text-gray-400">
                  {getText('gender_distribution')}
                </span>
                <span className="text-gray-900 dark:text-white">
                  {Math.round(metrics?.malePercentage ?? 0)}% {getText('male')}
                  <span className="mx-2">|</span>
                  {Math.round(metrics?.femalePercentage ?? 0)}% {getText('female')}
                </span>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{
                    width: `${Math.round(metrics?.malePercentage ?? 0)}%`,
                  }}
                ></div>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div
                  className="bg-pink-500 h-2 rounded-full"
                  style={{
                    width: `${Math.round(metrics?.femalePercentage ?? 0)}%`,
                  }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600 dark:text-gray-400">{getText('age_groups')}</span>
                <span className="text-gray-900 dark:text-white">{getText('count')}</span>
              </div>
              <div className="space-y-2">
                {Object.entries(metrics?.ageGroups || {}).map(
                  ([range, count]) =>
                    range !== '_id' && (
                      <div key={range} className="flex items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400 w-20">
                          {range}
                        </span>
                        <div className="flex-1 mx-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-500 h-2 rounded-full"
                              style={{
                                width: `${metrics.totalUsers > 0 ? (count / metrics.totalUsers) * 100 : 0}%`,
                              }}
                            ></div>
                          </div>
                        </div>
                        <span className="text-sm text-gray-900 dark:text-white w-10">{count}</span>
                      </div>
                    )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Performance Summary */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {getText('performance_summary')}
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">
                {getText('average_response_time')}
              </span>
              <span className="font-medium">
                {Math.round(metrics.averageResponseTime / (1000 * 60))} {getText('minute')}
              </span>
            </div>
            {false && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Satisfaction Rate</span>
                <span className="font-medium">{Math.round(metrics.patientSatisfaction)}%</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">{getText('completion_rate')}</span>
              <span className="font-medium">
                {metrics.totalTickets > 0
                  ? Math.round((metrics.completedTickets / metrics.totalTickets) * 100)
                  : 0}
                %
              </span>
            </div>
          </div>
        </div>

        {/* Date Range Summary */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {getText('date_range_summary')}
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">{getText('time_period')}</span>
              <span className="font-medium">
                {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">{getText('total_days')}</span>
              <span className="font-medium">
                {Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">{getText('grouping')}</span>
              <span className="font-medium capitalize">{getText(dateRange)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
